/**
 * Registro del service worker.
 *
 * Es lo que convierte a FC Cloud en una app instalable que abre sin conexión:
 * en Android queda con ícono propio desde "Instalar app" de Chrome, y en iPhone
 * desde "Agregar a pantalla de inicio" de Safari. En los dos casos sin pasar
 * por ninguna tienda.
 */

/** El service worker sólo corre en contexto seguro: HTTPS, o localhost. */
function contextoSeguro() {
  return window.isSecureContext
    || window.location.hostname === 'localhost'
    || window.location.hostname === '127.0.0.1';
}

/**
 * Registra el service worker si el entorno lo permite.
 *
 * En desarrollo no se registra a propósito: un service worker sirviendo bundles
 * cacheados encima del hot reload es una fuente clásica de "cambié el código y
 * no se ve", con media hora perdida antes de darse cuenta. Para probar el modo
 * offline hay que levantar el build (`npm run build` y servir `build/`).
 *
 * `alHaberVersionNueva` se llama cuando ya hay una versión descargada esperando
 * para activarse, para poder ofrecer "Actualizar" en vez de recargar de prepo:
 * una recarga forzada en medio de un formulario le borra a alguien lo que venía
 * cargando en la obra.
 */
/**
 * Versión del service worker que está sirviendo esta pantalla.
 *
 * Devuelve `null` si no hay ninguno controlando —desarrollo, o la primera
 * visita antes de que tome el control—, lo que también es información: quiere
 * decir que la app está corriendo como una página web común, sin modo offline.
 *
 * Existe porque al probar en un teléfono real no había forma de distinguir el
 * build nuevo de uno cacheado de antes. Sin ese dato, un bug ya arreglado
 * parece seguir vivo y se pierde la tarde buscándolo de nuevo.
 */
export function versionDelServiceWorker(timeoutMs = 1500) {
  return new Promise((resolve) => {
    const sw = navigator.serviceWorker?.controller;
    if (!sw) {
      resolve(null);
      return;
    }

    const canal = new MessageChannel();
    const vencido = setTimeout(() => resolve(null), timeoutMs);
    canal.port1.onmessage = (evento) => {
      clearTimeout(vencido);
      resolve(evento.data?.version || null);
    };

    try {
      sw.postMessage({ tipo: 'VERSION' }, [canal.port2]);
    } catch {
      clearTimeout(vencido);
      resolve(null);
    }
  });
}

export function registrarServiceWorker({ alHaberVersionNueva } = {}) {
  if (!('serviceWorker' in navigator)) return;
  if (process.env.NODE_ENV !== 'production') return;
  if (!contextoSeguro()) return;

  window.addEventListener('load', async () => {
    try {
      const registro = await navigator.serviceWorker.register('/sw.js');

      registro.addEventListener('updatefound', () => {
        const nuevo = registro.installing;
        if (!nuevo) return;

        nuevo.addEventListener('statechange', () => {
          // `controller` presente significa que ya había una versión corriendo:
          // esto es una actualización, no la primera instalación.
          if (nuevo.state === 'installed' && navigator.serviceWorker.controller) {
            alHaberVersionNueva?.(() => {
              nuevo.postMessage({ tipo: 'ACTIVAR_AHORA' });
            });
          }
        });
      });
    } catch (error) {
      // Que falle el registro no puede tumbar la app: sin service worker sigue
      // andando con red, que es como funcionaba antes de todo esto.
      console.warn('[FC Cloud] No se pudo registrar el service worker:', error);
    }
  });
}

export default registrarServiceWorker;
